import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Fuel, Paperclip, PieChart as PieIcon, Plus, Receipt, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Pagination, Skeleton, Spinner, Table, Td, Th, useToast } from '../../components/ui';
import { api, qs, type Paginated } from '../../lib/api';
import { fmtDate, fmtDateTime, fmtEgp, fmtNumber } from '../../lib/format';
import { useSocketEvent } from '../../lib/socket';
import type { VehicleRow } from '../vehicles/VehiclesPage';
import { ExpenseFormDialog } from './ExpenseFormDialog';
import { catName, expenseTone, type ExpenseAnalytics, type ExpenseCategory, type ExpenseRow, type FuelConsumption } from './types';

const STATUS_TABS = ['', 'PENDING', 'APPROVED', 'REJECTED'] as const;
const VIEWS = ['list', 'analytics', 'fuel'] as const;
const COLORS = ['#0F3D26', '#16A34A', '#F59E0B', '#2563EB', '#EF4444', '#7C3AED', '#94A3B8'];

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

export function ExpensesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const view = (VIEWS as readonly string[]).includes(params.get('view') ?? '') ? (params.get('view') as (typeof VIEWS)[number]) : 'list';
  const [adding, setAdding] = useState(false);
  useSocketEvent('expense.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['expenses'] }), [qc]));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('nav.expenses')}
        actions={
          <Can perm="expenses:create">
            <button className="btn-primary" onClick={() => setAdding(true)}>
              <Plus size={16} /> {t('expenses.add')}
            </button>
          </Can>
        }
      />
      <div className="flex flex-wrap gap-1 rounded-xl bg-surface-2 p-1 self-start" role="tablist">
        {VIEWS.map((v) => (
          <button key={v} role="tab" aria-selected={view === v} className={clsx('flex min-h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors', view === v ? 'bg-surface text-brand-ink shadow-sm' : 'text-text-muted hover:text-text')} onClick={() => setParams({ view: v })}>
            {v === 'list' ? <Receipt size={14} /> : v === 'analytics' ? <PieIcon size={14} /> : <Fuel size={14} />} {t(`expenses.views.${v}`)}
          </button>
        ))}
      </div>
      {view === 'list' ? <ExpensesList /> : view === 'analytics' ? <ExpensesAnalytics /> : <FuelView />}
      <ExpenseFormDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function ExpensesList() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [status, setStatus] = useState<(typeof STATUS_TABS)[number]>('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rejecting, setRejecting] = useState<ExpenseRow | null>(null);
  const [reason, setReason] = useState('');

  const categories = useQuery({ queryKey: ['expense-categories'], queryFn: () => api<ExpenseCategory[]>('/expenses/categories') });
  const vehicles = useQuery({ queryKey: ['vehicles-lite'], queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200&status=ACTIVE').then((r) => r.data) });
  const q = useQuery({
    queryKey: ['expenses', page, search, status, categoryId, vehicleId, from, to],
    queryFn: () => api<Paginated<ExpenseRow> & { meta: { sumEgp: number } }>(`/expenses${qs({ page, q: search, status: status || undefined, categoryId: categoryId || undefined, vehicleId: vehicleId || undefined, from: from || undefined, to: to || undefined })}`),
  });
  const review = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: 'approve' | 'reject'; reason?: string }) => api(`/expenses/${id}/${action}`, { method: 'POST', json: reason ? { reason } : {} }),
    onSuccess: () => {
      toast.success(t('common.saved'));
      setRejecting(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: toast.error,
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl bg-surface-2 p-1" role="tablist">
          {STATUS_TABS.map((s) => (
            <button key={s} role="tab" aria-selected={status === s} className={clsx('min-h-9 rounded-lg px-3 text-sm font-semibold transition-colors', status === s ? 'bg-surface text-brand-ink shadow-sm' : 'text-text-muted hover:text-text')} onClick={() => { setStatus(s); setPage(1); }}>
              {s ? t(`expenses.statuses.${s}`) : t('common.all')}
            </button>
          ))}
        </div>
        <input className="input max-w-[200px]" placeholder={t('common.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} aria-label={t('common.search')} />
        <select className="input max-w-[170px]" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(1); }} aria-label={t('expenses.category')}>
          <option value="">{t('expenses.allCategories')}</option>
          {(categories.data ?? []).map((c) => <option key={c.id} value={c.id}>{catName(c, i18n.language)}</option>)}
        </select>
        <select className="input max-w-[170px]" value={vehicleId} onChange={(e) => { setVehicleId(e.target.value); setPage(1); }} aria-label={t('expenses.vehicle')}>
          <option value="">{t('expenses.allVehicles')}</option>
          {(vehicles.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.plateAr}</option>)}
        </select>
        <input type="date" dir="ltr" className="input max-w-[150px]" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} aria-label={t('expenses.from')} />
        <input type="date" dir="ltr" className="input max-w-[150px]" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} aria-label={t('expenses.to')} />
        {q.data ? <span className="ms-auto text-sm text-text-muted">{t('common.totalCount', { count: q.data.meta.total })} · <strong className="text-text">{fmtEgp(q.data.meta.sumEgp, i18n.language)}</strong></span> : null}
      </div>

      {q.isLoading ? (
        <Skeleton className="h-72" />
      ) : !q.data?.data.length ? (
        <div className="card"><EmptyState title={t('expenses.empty')} hint={status === 'PENDING' ? t('expenses.emptyPendingHint') : undefined} /></div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('expenses.number')}</Th>
              <Th>{t('expenses.occurredAt')}</Th>
              <Th>{t('expenses.category')}</Th>
              <Th>{t('expenses.vehicle')}</Th>
              <Th>{t('expenses.driver')}</Th>
              <Th>{t('expenses.trip')}</Th>
              <Th className="text-end">{t('expenses.amount')}</Th>
              <Th>{t('expenses.status')}</Th>
              <Th className="w-1" sticky="end" />
            </tr>
          </thead>
          <tbody>
            {q.data.data.map((e) => (
              <tr key={e.id} className="group hover:bg-surface-hover">
                <Td>
                  <Link to={`/expenses/${e.id}`} className="font-bold text-brand-ink hover:underline" dir="ltr">{e.number}</Link>
                  {e.receiptFileId ? <Paperclip size={12} className="ms-1 inline text-text-faint" aria-label={t('expenses.receipt')} /> : null}
                </Td>
                <Td className="text-text-muted">{fmtDateTime(e.occurredAt, i18n.language)}</Td>
                <Td>
                  {catName(e.category, i18n.language)}
                  {e.fuelLog ? <div className="text-xs text-text-muted" dir="ltr">{fmtNumber(e.fuelLog.liters, i18n.language, 1)} {t('units.liter')}{e.fuelLog.lPer100Km ? ` · ${fmtNumber(e.fuelLog.lPer100Km, i18n.language, 1)} L/100km` : ''}</div> : null}
                </Td>
                <Td>{e.vehicle ? <Link to={`/vehicles/${e.vehicle.id}`} className="hover:underline">{e.vehicle.plateAr}</Link> : '—'}</Td>
                <Td>{e.driver ? <Link to={`/drivers/${e.driver.id}`} className="hover:underline">{e.driver.fullName}</Link> : '—'}</Td>
                <Td>{e.trip ? <Link to={`/trips/${e.trip.id}`} className="hover:underline" dir="ltr">{e.trip.number}</Link> : '—'}</Td>
                <Td className="text-end font-bold tabular-nums">{fmtEgp(e.amountEgp, i18n.language)}</Td>
                <Td>
                  <Badge tone={expenseTone(e.status)} dot>{t(`expenses.statuses.${e.status}`)}</Badge>
                  {e.isRebillable ? <Badge tone="info">{t('expenses.rebillableShort')}</Badge> : null}
                </Td>
                <Td sticky="end">
                  {e.status === 'PENDING' ? (
                    <Can perm="expenses:approve">
                      <div className="flex justify-end gap-1">
                        <button className="btn-ghost size-9 !p-0 text-success-fg" aria-label={t('expenses.approve')} title={t('expenses.approve')} disabled={review.isPending} onClick={() => review.mutate({ id: e.id, action: 'approve' })}><Check size={16} /></button>
                        <button className="btn-ghost size-9 !p-0 text-danger-fg" aria-label={t('expenses.reject')} title={t('expenses.reject')} onClick={() => setRejecting(e)}><X size={16} /></button>
                      </div>
                    </Can>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={9}><Pagination page={q.data.meta.page} pageSize={q.data.meta.pageSize} total={q.data.meta.total} onPage={setPage} /></td></tr></tfoot>
        </Table>
      )}

      <Dialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={t('expenses.reject')}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setRejecting(null)}>{t('common.cancel')}</button>
            <button className="btn-primary !bg-danger" disabled={reason.trim().length < 2 || review.isPending} onClick={() => rejecting && review.mutate({ id: rejecting.id, action: 'reject', reason: reason.trim() })}>
              {review.isPending ? <Spinner /> : <X size={16} />} {t('expenses.reject')}
            </button>
          </>
        }
      >
        <p className="mb-3 text-sm text-text-muted">{rejecting?.number} · {fmtEgp(rejecting?.amountEgp, i18n.language)}</p>
        <Field label={t('expenses.rejectReason')} required>{(id) => <textarea id={id} className="input min-h-24" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />}</Field>
      </Dialog>
    </>
  );
}

function ExpensesAnalytics() {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const q = useQuery({ queryKey: ['expenses', 'analytics', from, to], queryFn: () => api<ExpenseAnalytics>(`/expenses/analytics${qs({ from: from || undefined, to: to || undefined })}`) });
  const a = q.data;
  const money = (v: number) => fmtEgp(v, i18n.language);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" dir="ltr" className="input max-w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t('expenses.from')} />
        <span className="text-text-faint">→</span>
        <input type="date" dir="ltr" className="input max-w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} aria-label={t('expenses.to')} />
      </div>
      {q.isLoading || !a ? (
        <Skeleton className="h-72" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={t('expenses.kpi.total')} value={money(a.totalEgp)} hint={t('common.totalCount', { count: a.count })} />
            <Kpi label={t('expenses.kpi.fuel')} value={money(a.fuelEgp)} hint={a.totalEgp ? `${Math.round((a.fuelEgp / a.totalEgp) * 100)}%` : undefined} />
            <Kpi label={t('expenses.kpi.pending')} value={money(a.pendingEgp)} hint={t('common.totalCount', { count: a.pendingCount })} tone="warning" />
            <Kpi label={t('expenses.kpi.avgPerVehicle')} value={a.byVehicle.length ? money(a.totalEgp / a.byVehicle.length) : '—'} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <section className="card lg:col-span-2">
              <h2 className="mb-3 text-sm font-bold text-text-2">{t('expenses.charts.byMonth')}</h2>
              {a.byMonth.length ? (
                <div className="h-64" dir="ltr">
                  <ResponsiveContainer>
                    <BarChart data={a.byMonth}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5EE" />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} tickFormatter={(v: number) => fmtNumber(v, i18n.language)} />
                      <Tooltip formatter={(v: number) => money(v)} />
                      <Legend />
                      <Bar dataKey="total" name={t('expenses.kpi.total')} fill="#0F3D26" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="fuel" name={t('expenses.kpi.fuel')} fill="#BEF264" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyState title={t('common.empty')} />}
            </section>
            <section className="card">
              <h2 className="mb-3 text-sm font-bold text-text-2">{t('expenses.charts.byCategory')}</h2>
              {a.byCategory.length ? (
                <>
                  <div className="h-44" dir="ltr">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={a.byCategory} dataKey="total" nameKey={i18n.language === 'en' ? 'nameEn' : 'nameAr'} innerRadius={45} outerRadius={70} paddingAngle={2}>
                          {a.byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => money(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="flex flex-col gap-1 text-sm">
                    {a.byCategory.map((c, i) => (
                      <li key={c.id} className="flex items-center gap-2">
                        <span className="size-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} aria-hidden />
                        <span className="flex-1">{catName(c, i18n.language)}</span>
                        <span className="font-semibold tabular-nums">{money(c.total)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : <EmptyState title={t('common.empty')} />}
            </section>
            <section className="card">
              <h2 className="mb-3 text-sm font-bold text-text-2">{t('expenses.charts.byVehicle')}</h2>
              <RankList rows={a.byVehicle.map((v) => ({ id: v.id, label: v.plateAr, total: v.total, count: v.count, to: `/vehicles/${v.id}` }))} max={a.byVehicle[0]?.total ?? 0} money={money} />
            </section>
            <section className="card">
              <h2 className="mb-3 text-sm font-bold text-text-2">{t('expenses.charts.byDriver')}</h2>
              <RankList rows={a.byDriver.map((d) => ({ id: d.id, label: d.fullName, total: d.total, count: d.count, to: `/drivers/${d.id}` }))} max={a.byDriver[0]?.total ?? 0} money={money} />
            </section>
          </div>
        </>
      )}
    </>
  );
}

function FuelView() {
  const { t, i18n } = useTranslation();
  const [vehicleId, setVehicleId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const vehicles = useQuery({ queryKey: ['vehicles-lite'], queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200&status=ACTIVE').then((r) => r.data) });
  const q = useQuery({ queryKey: ['expenses', 'fuel', vehicleId, from, to], queryFn: () => api<FuelConsumption>(`/fuel/consumption${qs({ vehicleId: vehicleId || undefined, from: from || undefined, to: to || undefined })}`) });
  const s = q.data?.summary;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <select className="input max-w-[200px]" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} aria-label={t('expenses.vehicle')}>
          <option value="">{t('expenses.allVehicles')}</option>
          {(vehicles.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.plateAr}</option>)}
        </select>
        <input type="date" dir="ltr" className="input max-w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} aria-label={t('expenses.from')} />
        <input type="date" dir="ltr" className="input max-w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} aria-label={t('expenses.to')} />
      </div>
      {s ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Kpi label={t('expenses.fuel.fills')} value={fmtNumber(s.fills, i18n.language)} />
          <Kpi label={t('expenses.fuel.liters')} value={`${fmtNumber(s.liters, i18n.language, 1)} ${t('units.liter')}`} />
          <Kpi label={t('expenses.fuel.cost')} value={fmtEgp(s.amountEgp, i18n.language)} />
          <Kpi label={t('expenses.fuel.avgL100')} value={s.avgLPer100Km != null ? `${fmtNumber(s.avgLPer100Km, i18n.language, 1)} L/100km` : '—'} hint={s.km ? `${fmtNumber(s.km, i18n.language)} ${t('units.km')}` : undefined} />
          <Kpi label={t('expenses.fuel.avgEgpKm')} value={s.avgEgpPerKm != null ? `${fmtNumber(s.avgEgpPerKm, i18n.language, 2)} ${t('expenses.fuel.egpPerKm')}` : '—'} />
        </div>
      ) : null}
      {q.isLoading ? (
        <Skeleton className="h-72" />
      ) : !q.data?.items.length ? (
        <div className="card"><EmptyState title={t('expenses.fuel.empty')} hint={t('expenses.fuel.emptyHint')} /></div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('expenses.occurredAt')}</Th>
              <Th>{t('expenses.vehicle')}</Th>
              <Th>{t('expenses.driver')}</Th>
              <Th className="text-end">{t('expenses.liters')}</Th>
              <Th className="text-end">{t('expenses.pricePerLiter')}</Th>
              <Th className="text-end">{t('expenses.amount')}</Th>
              <Th className="text-end">{t('expenses.odometerKm')}</Th>
              <Th className="text-end">{t('expenses.fuel.kmSincePrev')}</Th>
              <Th className="text-end">L/100km</Th>
              <Th className="text-end">{t('expenses.fuel.egpPerKm')}</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((i) => (
              <tr key={i.expenseId} className="hover:bg-surface-hover">
                <Td><Link to={`/expenses/${i.expenseId}`} className="text-brand-ink hover:underline">{fmtDate(i.occurredAt, i18n.language)}</Link>{!i.isFullTank ? <div className="text-xs text-text-faint">{t('expenses.partialFill')}</div> : null}</Td>
                <Td>{i.vehicle.plateAr}</Td>
                <Td>{i.driver?.fullName ?? '—'}</Td>
                <Td className="text-end tabular-nums">{fmtNumber(i.liters, i18n.language, 1)}</Td>
                <Td className="text-end tabular-nums">{fmtNumber(i.pricePerLiter, i18n.language, 2)}</Td>
                <Td className="text-end font-semibold tabular-nums">{fmtEgp(i.amountEgp, i18n.language)}</Td>
                <Td className="text-end tabular-nums">{i.odometerKm != null ? fmtNumber(i.odometerKm, i18n.language) : '—'}</Td>
                <Td className="text-end tabular-nums">{i.kmSincePrev != null ? fmtNumber(i.kmSincePrev, i18n.language) : '—'}</Td>
                <Td className={clsx('text-end font-bold tabular-nums', i.lPer100Km != null && i.lPer100Km > 40 ? 'text-danger-fg' : '')}>{i.lPer100Km != null ? fmtNumber(i.lPer100Km, i18n.language, 1) : '—'}</Td>
                <Td className="text-end tabular-nums">{i.egpPerKm != null ? fmtNumber(i.egpPerKm, i18n.language, 2) : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warning' }) {
  return (
    <div className={clsx('card !p-4', tone === 'warning' ? 'border-warning/40 bg-warning-bg/40' : '')}>
      <div className="text-xs font-semibold text-text-muted">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
      {hint ? <div className="text-xs text-text-faint">{hint}</div> : null}
    </div>
  );
}

function RankList({ rows, max, money }: { rows: { id: string; label: string; total: number; count: number; to: string }[]; max: number; money: (n: number) => string }) {
  const { t } = useTranslation();
  if (!rows.length) return <EmptyState title={t('common.empty')} />;
  return (
    <ul className="flex flex-col gap-2 text-sm">
      {rows.map((r) => (
        <li key={r.id}>
          <div className="flex items-center justify-between gap-2">
            <Link to={r.to} className="truncate hover:underline">{r.label}</Link>
            <span className="font-semibold tabular-nums">{money(r.total)}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-brand" style={{ width: `${max ? Math.max(4, (r.total / max) * 100) : 0}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}
